import topic from "dojo/topic";
import RequestManager from "more/RequestManager";

interface StoredTranslations{
    translations:Record<string,string>,
    expUtc:number,
}

interface ServerTranslations{
    currentLangCode:string,
    cacheExp:number,
    translations:Record<string,string>
}

class TranslationLoadingManager {
    //singleton zeug
    private static instance: TranslationLoadingManager;

    public static getInstance(): TranslationLoadingManager {
        if (!TranslationLoadingManager.instance) {
            TranslationLoadingManager.instance = new TranslationLoadingManager();
        }
        return TranslationLoadingManager.instance;
    }

    private constructor() {
        // Private constructor to prevent instantiation
        if (typeof window["translations"] != 'object'){
            window["translations"] = {};
        }
        //access zu localStorage testen
        try {
            window.localStorage.setItem("accessTest", "test");
            window.localStorage.removeItem("accessTest");
            this.storageInterface = window.localStorage; //wir verwenden local storage
        } catch {
            console.warn("translationLoadingManager: cannot access localStorage, falling back to sessionStorage!");
            this.storageInterface = window.sessionStorage; //fallback, besser als nix
        }
        this.loadTranslationsFromStorage();
    }

    // ### wirkliche Methoden

    //alle translation pre-fixes die im localStorage gespeichert werden sollen, am besten alles was im login zu sehen ist
    private expTime:number = 0;
    private storageInterface:Storage;
    public flagTranslationsReady:boolean = false;

    public deleteAllTranslationsFromLocalStorage(){
        //dev
        let keys:Array<string> = [];
        for (let i = 0; i < this.storageInterface.length; i++){
            let key = this.storageInterface.key(i);
            if (key.startsWith("eGo_translations__")){
                keys.push(key);
            }
        }

        for (let key of keys){
            try{
                try{
                    this.storageInterface.removeItem(key);
                }catch(e)
                {
                    console.error("translation loading manager: cannot remove localStorage item by key", key, e);
                }
            }catch{}
        }
    }

    public loadTranslationsFromStorage(){
        let cacheKey = "eGo_translations__" + window.currentLanguageCode;
        let jsonResult:string;
        try{
            jsonResult = this.storageInterface.getItem(cacheKey); // window.currentLanguageCode wird in cshtml gesetzt
        }catch(e){
            console.error("cannot read localStorage for translation cache", e);
            return; //security error?
        }
        if (typeof jsonResult == 'string'){
            let cachedTranslations:StoredTranslations = JSON.parse(jsonResult);
            this.expTime = cachedTranslations.expUtc;
            //translation laden
            window["translations"] = Object.assign({}, window["translations"], cachedTranslations.translations);
            this.flagTranslationsReady = true;
            topic.publish("TranslationLoadingManager/ready", true);
            if (this.isDebug() == true){
                this.deleteAllTranslationsFromLocalStorage();
            }
        }
        //kein valid json in localStorage
    }

    public loadTranslationsFromServer() {
        if ((this.expTime > Date.now()) && (this.isDebug() == false)) {
            this.loadTranslationsFromStorage();
            return; // wir laden nichts
        }
        let requestManager: RequestManager = new RequestManager();
        requestManager.get(
            window.location.origin + window.appRoot + "TranslationLoader?lang=" + window.currentLanguageCode,
            (response: ServerTranslations) => {
                if (window.currentLanguageCode != response.currentLangCode) {
                    console.error("TranslationLoadingManager: Language code mismatch. Expected:", window.currentLanguageCode, "Received:", response.currentLangCode);
                    return;
                }
                if (this.tryDetectIfRespIsInvalid(response?.translations) == true){
                    this.expTime = Date.now() - 1;
                }else{
                    this.expTime = Date.now() + ((response.cacheExp + 1 ) * 60 * 1000);
                    //wir ueberschreiben nur bei validen uebersetzungen
                    window["translations"] = Object.assign({}, window["translations"], response.translations);
                }
                this.flagTranslationsReady = true;
                topic.publish("TranslationLoadingManager/ready", true);
                try {
                    if (Object.keys(response.translations).length > 0)
                    {
                        this.storageInterface.setItem(
                            "eGo_translations__" + window.currentLanguageCode,
                            JSON.stringify({
                                translations: response.translations,
                                expUtc: this.expTime,
                            })
                        );
                    }else{
                        console.warn("no translations from server to use!");
                    }
                } catch (e) {
                    console.error("TranslationLoadingManager: Cannot write to localStorage", e);
                }
            },
            (error) => {
                console.error("TranslationLoadingManager: Error while fetching translations from server", error);
            }
        );
    }

    private tryDetectIfRespIsInvalid(translationObject:Record<string,string>):boolean{
        if (typeof translationObject != 'object'){
            return true;
        }
        let trObj = structuredClone(translationObject);
        let translationKeys: Array<string> = Object.keys(trObj);
        if (translationKeys.length < 10){ // wir gehen davon aus dass wir mindestens 10 uebersetzungen haben
            return true;
        }
        // ---
        let isInValid = false;

        //wir versuchen empty string translations zu finden / oder null
        //wir gehen davon aus das unabsichtlich ein paar translations leer sein koennen ("user error" bzw. eher "dev error")
        //deshalb ermitteln wir prozentual den wert
        let totalTranslationCount = 0;
        let totalEmptyTranslationCount = 0;
        for (let key of translationKeys){
            let translation = trObj[key];
            totalTranslationCount++;
            if (this.helper_isNullOrEmptyWhiteSpace(translation) == true){
                totalEmptyTranslationCount++;
            }
        }

        const MAX_BAD_TRANSLATION_RATIO = 0.25; // 25%

        let emptyPercentage:number = totalEmptyTranslationCount / totalTranslationCount;
        if (emptyPercentage > MAX_BAD_TRANSLATION_RATIO ){
            console.error("Potential bad translations received from server! Skipping saving translations!");
            isInValid = true;
        }


        return isInValid;
    }

    private helper_isNullOrEmptyWhiteSpace(str: string | null | undefined): boolean {
        return !str || str.trim() === "";
    }

    private isDebug(): boolean {
        const host = window.location.hostname;
        let isDebug = host === 'localhost' || host === '::1' || host.startsWith('127.') || host.endsWith('.local');
        if (isDebug) {
            const params = new URLSearchParams(window.location.search);
            if ((params?.getAll("translationDebug")?.length ?? 0 ) > 0){ //zum prod simulieren
                isDebug = false;
            }
        }
        return isDebug;
    }

}

window["debug_translationLoadingManager"] = TranslationLoadingManager.getInstance(); //so frueh wie moeglich translations laden, deshalb schon beim import

export default TranslationLoadingManager;
